import { THEME_DEFAULT_FONT_ID, applyFontOptionToTypography, isValidAppFontId } from './app-fonts.js';
import { getAppTheme } from './registry.js';
import { darkenHex, hexAlpha, hexToRgb, lightenHex, normalizeHexColor, parseCssPx, } from './theme-color-utils.js';
const MAX_RADIUS_PX = 40;
function accentDerivedShadows(accent) {
    const { r, g, b } = hexToRgb(accent);
    return {
        glowViolet: `0 8px 32px rgba(${r}, ${g}, ${b}, 0.12)`,
        channelRowActive: `0 0 0 1px rgba(${r}, ${g}, ${b}, 0.12), 0 4px 24px rgba(${r}, ${g}, ${b}, 0.08)`,
        composerFocus: `0 0 0 1px rgba(${r}, ${g}, ${b}, 0.15), 0 12px 40px rgba(59, 130, 246, 0.12)`,
        serverBannerGlow: `radial-gradient(ellipse 80% 60% at 50% 0%, rgba(${r}, ${g}, ${b}, 0.22) 0%, transparent 70%)`,
        composerGlow: `linear-gradient(90deg, rgba(${r}, ${g}, ${b}, 0.35), rgba(59, 130, 246, 0.3))`,
    };
}
function applyAccent(theme, accent) {
    theme.colors.accent.violet = accent;
    theme.colors.accent.purple = lightenHex(accent, 0.12);
    theme.colors.brand[500] = accent;
    theme.colors.brand[400] = lightenHex(accent, 0.18);
    theme.colors.brand[600] = darkenHex(accent, 0.12);
    theme.colors.brand[300] = lightenHex(accent, 0.32);
    theme.effects.ambient.violet = accent;
    const derived = accentDerivedShadows(accent);
    theme.shadows.glowViolet = derived.glowViolet;
    theme.shadows.channelRowActive = derived.channelRowActive;
    theme.shadows.composerFocus = derived.composerFocus;
    theme.gradients.serverBannerGlow = derived.serverBannerGlow;
    theme.gradients.composerGlow = derived.composerGlow;
}
function applyBackground(theme, bg) {
    theme.colors.base.deepest = bg;
    theme.colors.base.deep = lightenHex(bg, 0.04);
    theme.colors.base.DEFAULT = lightenHex(bg, 0.08);
    theme.colors.surface[900] = bg;
}
function applyPanelColor(theme, panel) {
    theme.colors.surface[800] = panel;
    theme.colors.surface[700] = darkenHex(panel, 0.06);
    theme.colors.surface[600] = darkenHex(panel, 0.12);
    theme.colors.surface[500] = darkenHex(panel, 0.18);
    const useGlass = theme.chrome.enableAmbientLights;
    if (useGlass) {
        theme.chrome.panelSidebarBg = hexAlpha(panel, 0.8);
        theme.chrome.panelMembersBg = hexAlpha(panel, 0.8);
        theme.chrome.panelChatBg = hexAlpha(theme.colors.surface[900], 0.6);
    }
    else {
        theme.chrome.panelSidebarBg = panel;
        theme.chrome.panelMembersBg = panel;
        theme.chrome.panelChatBg = theme.colors.surface[900];
    }
    const { r, g, b } = hexToRgb(theme.colors.base.deepest);
    theme.gradients.serverBannerOverlay = `linear-gradient(180deg, rgba(${r}, ${g}, ${b}, 0.15) 0%, rgba(${r}, ${g}, ${b}, 0.55) 55%, ${hexAlpha(panel, 0.98)} 100%)`;
}
/** Effective customization values for UI (saved overrides or base theme defaults). */
export function getThemeCustomizationDefaults(themeId, saved) {
    const base = getAppTheme(themeId);
    const savedFontId = saved?.fontId;
    return {
        accentColor: saved?.accentColor ?? base.colors.accent.violet,
        backgroundColor: saved?.backgroundColor ?? base.colors.base.deepest,
        panelColor: saved?.panelColor ?? base.colors.surface[800],
        panelBorderRadiusPx: saved?.panelBorderRadiusPx ?? parseCssPx(base.layout.panelBorderRadius, 0),
        composerBorderRadiusPx: saved?.composerBorderRadiusPx ?? parseCssPx(base.layout.composerBorderRadius, 8),
        enableDepthShadows: saved?.enableDepthShadows ?? base.chrome.enableDepthShadows,
        fontId: savedFontId && isValidAppFontId(savedFontId) ? savedFontId : THEME_DEFAULT_FONT_ID,
    };
}
/** Strips invalid keys and empty objects. */
export function normalizeAppThemeCustomization(input) {
    if (!input || typeof input !== 'object')
        return null;
    const out = {};
    if (input.accentColor != null) {
        const hex = normalizeHexColor(String(input.accentColor));
        if (hex)
            out.accentColor = hex;
    }
    if (input.backgroundColor != null) {
        const hex = normalizeHexColor(String(input.backgroundColor));
        if (hex)
            out.backgroundColor = hex;
    }
    if (input.panelColor != null) {
        const hex = normalizeHexColor(String(input.panelColor));
        if (hex)
            out.panelColor = hex;
    }
    if (input.panelBorderRadiusPx != null) {
        const n = Math.round(Number(input.panelBorderRadiusPx));
        if (Number.isFinite(n))
            out.panelBorderRadiusPx = Math.max(0, Math.min(MAX_RADIUS_PX, n));
    }
    if (input.composerBorderRadiusPx != null) {
        const n = Math.round(Number(input.composerBorderRadiusPx));
        if (Number.isFinite(n))
            out.composerBorderRadiusPx = Math.max(0, Math.min(MAX_RADIUS_PX, n));
    }
    if (input.enableDepthShadows != null) {
        out.enableDepthShadows = Boolean(input.enableDepthShadows);
    }
    if (input.fontId != null) {
        const id = String(input.fontId).trim();
        if (id !== THEME_DEFAULT_FONT_ID && isValidAppFontId(id)) {
            out.fontId = id;
        }
    }
    return Object.keys(out).length > 0 ? out : null;
}
export function hasAppThemeCustomization(customization) {
    return normalizeAppThemeCustomization(customization) !== null;
}
/** Deep-clones base theme and applies optional user customization. */
export function resolveUserAppTheme(themeId, customization) {
    const base = getAppTheme(themeId);
    const theme = JSON.parse(JSON.stringify(base));
    const custom = normalizeAppThemeCustomization(customization);
    if (!custom)
        return theme;
    if (custom.accentColor)
        applyAccent(theme, custom.accentColor);
    if (custom.backgroundColor)
        applyBackground(theme, custom.backgroundColor);
    if (custom.panelColor)
        applyPanelColor(theme, custom.panelColor);
    if (custom.panelBorderRadiusPx != null) {
        theme.layout.panelBorderRadius = `${custom.panelBorderRadiusPx}px`;
    }
    if (custom.composerBorderRadiusPx != null) {
        theme.layout.composerBorderRadius = `${custom.composerBorderRadiusPx}px`;
    }
    if (custom.enableDepthShadows != null) {
        theme.chrome.enableDepthShadows = custom.enableDepthShadows;
        applyDepthShadowMode(theme);
    }
    if (custom.fontId) {
        applyFontOptionToTypography(theme.typography, custom.fontId);
    }
    return theme;
}
/** Applies flat vs elevated shadow tokens from chrome.enableDepthShadows. */
export function applyDepthShadowMode(theme) {
    const { shadows, chrome } = theme;
    if (chrome.enableDepthShadows) {
        const ref = getAppTheme(theme.id);
        shadows.depthPanel = ref.shadows.depthPanel;
        shadows.depthButton = ref.shadows.depthButton;
        shadows.depthControl = ref.shadows.depthControl;
        shadows.panel = ref.shadows.depthPanel;
        if (shadows.channelRowActive === 'none') {
            const derived = accentDerivedShadows(theme.colors.accent.violet);
            shadows.channelRowActive = derived.channelRowActive;
            shadows.composerFocus = derived.composerFocus;
        }
        return;
    }
    shadows.panel = 'none';
    shadows.depthPanel = 'none';
    shadows.depthButton = 'none';
    shadows.depthControl = 'none';
    shadows.channelRowActive = 'none';
    shadows.composerFocus = 'none';
}
/** Builds customization payload with only values that differ from the base theme. */
export function compactAppThemeCustomization(themeId, values) {
    const defaults = getThemeCustomizationDefaults(themeId, null);
    const out = {};
    const accent = normalizeHexColor(values.accentColor);
    if (accent && accent !== normalizeHexColor(defaults.accentColor))
        out.accentColor = accent;
    const bg = normalizeHexColor(values.backgroundColor);
    if (bg && bg !== normalizeHexColor(defaults.backgroundColor))
        out.backgroundColor = bg;
    const panel = normalizeHexColor(values.panelColor);
    if (panel && panel !== normalizeHexColor(defaults.panelColor))
        out.panelColor = panel;
    if (values.panelBorderRadiusPx !== defaults.panelBorderRadiusPx) {
        out.panelBorderRadiusPx = values.panelBorderRadiusPx;
    }
    if (values.composerBorderRadiusPx !== defaults.composerBorderRadiusPx) {
        out.composerBorderRadiusPx = values.composerBorderRadiusPx;
    }
    if (values.enableDepthShadows !== defaults.enableDepthShadows) {
        out.enableDepthShadows = values.enableDepthShadows;
    }
    if (values.fontId !== defaults.fontId) {
        out.fontId = values.fontId;
    }
    return normalizeAppThemeCustomization(out);
}
//# sourceMappingURL=merge-theme-customization.js.map